import { Component, inject, input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FunctionAuthStrategy } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { Observable, take } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ConsentAdminApiService } from '@vendasta/sso';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { MatIconModule } from '@angular/material/icon';

type AuthStrategyType = 'unspecified' | 'platformManaged' | 'impersonation' | 'connectedIntegration';

export type AiToolAuthStrategyFormControls = {
  type: FormControl<string | null>;
  requiredScopes: FormControl<string[] | null>;
  connectedIntegrationId: FormControl<string | null>;
};

export class AiToolAuthStrategyForm extends FormGroup<AiToolAuthStrategyFormControls> {
  constructor(authStrategy?: FunctionAuthStrategy) {
    let type: AuthStrategyType = 'unspecified';
    let requiredScopes: string[] = [];
    let connectedIntegrationId = '';

    if (authStrategy) {
      if (authStrategy.platformManaged) {
        type = 'platformManaged';
        requiredScopes = authStrategy.platformManaged.requiredScopes || [];
      } else if (authStrategy.impersonation) {
        type = 'impersonation';
        requiredScopes = authStrategy.impersonation.requiredScopes || [];
      } else if (authStrategy.connectedIntegration) {
        type = 'connectedIntegration';
        connectedIntegrationId = authStrategy.connectedIntegration.connectionId || '';
      }
    }
    super({
      type: new FormControl(type, Validators.required),
      requiredScopes: new FormControl<string[]>(requiredScopes),
      connectedIntegrationId: new FormControl<string>(connectedIntegrationId),
    });
  }

  public get authStrategy(): FunctionAuthStrategy {
    const { type, requiredScopes, connectedIntegrationId } = this.getRawValue();
    switch (type) {
      case 'platformManaged':
        return { platformManaged: { requiredScopes: requiredScopes || [] } };
      case 'impersonation':
        return { impersonation: { requiredScopes: requiredScopes || [] } };
      case 'connectedIntegration':
        return { connectedIntegration: { connectionId: connectedIntegrationId || '' } };
      default:
        return { unspecified: {} };
    }
  }
}

@Component({
  selector: 'ai-tool-auth-strategy-form',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyTooltipModule,
    MatIconModule,
  ],
  templateUrl: './ai-tool-auth-strategy-form.component.html',
  styleUrl: './ai-tool-auth-strategy-form.component.scss',
})
export class AiToolAuthStrategyFormComponent {
  form = input.required<AiToolAuthStrategyForm>();
  readonly showScopes = input<boolean>(false);
  private scopes = inject(ConsentAdminApiService);

  scopeOptions$: Observable<string[]> = this.scopes.listScopes({ pageSize: 1000 }).pipe(
    take(1),
    map((resp) => resp?.scopes?.map((s) => s.id)),
    shareReplay(),
  );

  typeChange(type: AuthStrategyType) {
    if ((type == 'platformManaged' || type == 'impersonation') && this.showScopes()) {
      this.form().controls.requiredScopes.setValidators(Validators.required);
    } else {
      this.form().controls.requiredScopes.clearValidators();
    }
    this.form().controls.requiredScopes.updateValueAndValidity();
  }
}
